/**
 * SearchRunStatusPanel (Fix 2026-09-19, ТЗ §A2) — панель «Текущий запуск».
 *
 * Пользователь всегда видит: какой запуск идёт (короткий ID), его режим,
 * scope (сколько строк из датасета), какие именно строки выбраны и статус.
 * Сырые UUID не показываются — только первые 8 символов (полный ID — в
 * tooltip). Панель живёт над таблицей: режим тестового запуска (результаты
 * не сохраняются) больше не может пройти незамеченным.
 */
import { useTranslation } from 'react-i18next';
import { Radar } from 'lucide-react';

import type { PipelineRun } from '@/hooks/useGeneration';
import { cn } from '@/lib/utils';

function shortId(id: string): string {
  return id.slice(0, 8);
}

function rowsLabel(shown: number[], rest: number): string {
  const base = shown.map((n) => `#${n}`).join(', ');
  return rest > 0 ? `${base} +${rest}` : base;
}

export function SearchRunStatusPanel({
  pipeline,
  datasetTotal,
  rows,
}: {
  pipeline: PipelineRun;
  /** Полный размер датасета (truth /search/results total_rows). */
  datasetTotal?: number;
  /** Строки датасета (для маппинга scope_row_ids → номера #). */
  rows: { id: string; row_index: number }[];
}) {
  const { t } = useTranslation();

  const status = pipeline.status;
  const active = status === 'running' || status === 'queued' || status === 'paused';

  // Режим: тестовый / rerun / обычный с перезаписью / обычный.
  const modeKey = pipeline.is_test_run
    ? 'test'
    : pipeline.search_trigger === 'rerun'
      ? 'rerun'
      : pipeline.overwrite_existing
        ? 'overwrite'
        : 'normal';

  const indexById = new Map(rows.map((r) => [r.id, r.row_index]));
  const scopeIds = pipeline.scope_row_ids ?? [];
  const scopeNumbers = scopeIds
    .map((id) => indexById.get(id))
    .filter((n): n is number => n !== undefined)
    .sort((a, b) => a - b);
  const shown = scopeNumbers.slice(0, 8);
  const rest = scopeNumbers.length - shown.length;
  const label = rowsLabel(shown, rest);

  const statusKey =
    status === 'running' || status === 'queued'
      ? 'running'
      : status === 'paused'
        ? 'paused'
        : status === 'completed'
          ? 'completed'
          : status === 'failed'
            ? 'failed'
            : 'cancelled';

  return (
    <div
      data-testid="search-run-panel"
      className={cn(
        'card px-4 py-3 space-y-1.5 text-sm',
        active ? 'border border-primary-500/40' : 'opacity-90',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-1.5 font-semibold text-surface-100">
          <Radar className={cn('w-4 h-4', active ? 'text-primary-400' : 'text-surface-500')} />
          {t('search.run.title', { defaultValue: 'Current run' })}
        </span>
        <span
          className="font-mono text-xs px-1.5 py-0.5 rounded bg-surface-800 text-surface-300 cursor-help"
          title={pipeline.id}
        >
          {shortId(pipeline.id)}
        </span>
        <span
          className={cn(
            'text-xs px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap',
            pipeline.is_test_run
              ? 'bg-info-tint/15 text-info'
              : modeKey === 'rerun'
                ? 'bg-warning-tint/15 text-warning'
                : 'bg-primary-500/15 text-primary-300',
          )}
        >
          {t(`search.run.mode.${modeKey}`, { defaultValue: modeKey })}
        </span>
        <span
          className={cn(
            'text-xs px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap',
            status === 'completed' && 'bg-success-tint/15 text-success',
            status === 'failed' && 'bg-danger-tint/15 text-danger',
            (status === 'running' || status === 'queued') && 'bg-primary-500/15 text-primary-300',
            (status === 'paused' || status === 'cancelled') && 'bg-surface-700/40 text-surface-300',
          )}
        >
          {t(`search.run.status.${statusKey}`, { defaultValue: status })}
        </span>
        {active && pipeline.total_rows > 0 && (
          <span className="text-xs text-surface-400 font-mono tabular-nums">
            {pipeline.processed_rows}/{pipeline.total_rows}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-surface-400">
        <span>
          {t('search.run.scope', {
            scope: pipeline.total_rows,
            total: datasetTotal ?? pipeline.total_rows,
            defaultValue: `Scope: ${pipeline.total_rows} of ${datasetTotal ?? pipeline.total_rows} rows`,
          })}
        </span>
        {scopeNumbers.length > 0 && (
          <span className="font-mono cursor-help" title={scopeIds.join('\n')}>
            {t('search.run.rows', {
              rows: label,
              defaultValue: `Rows: ${label}`,
            })}
          </span>
        )}
      </div>

      {pipeline.is_test_run && (
        <div className="text-xs text-info">
          {t('search.run.testRunNote', {
            defaultValue: 'Test run results are not saved as final.',
          })}
        </div>
      )}
    </div>
  );
}